import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Paper,
  Select,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
  Group,
  Divider,
  Radio,
} from '@mantine/core';
import { SearchIcon } from 'lucide-react';
import { useUserSelectOptions } from '@/components/hooks';
import { notifications } from '@mantine/notifications';
import {
  getAragCaseByCaseAssistNumber,
  getAragCasesByMemberId,
  type GetAragCasesByMemberId,
  type CaseDetailsPage,
} from '@/cloudFunctions/arag';
import {
  AddEditMatterFormData,
  FormInsuranceCase,
} from '../AddEditMatter/types';
import { v4 as uuidv4 } from 'uuid';
import { PhoneNumber } from '@/database/baseTypes';
import { getStateNameFromCode } from '@/optionSets';
import { CopyableText } from '../CopyableText/CopyableText';
import { telemetry } from '@/lib/telemetry';
import { InsuranceCaseDocument, MatterDocument } from '@shared/types';
import styles from './AragSearchModal.module.css';
import { FlattenedMatter } from '@/database/aggregateTypes';
import { getFlattenedMatter } from '@/database/getFlattenedMatter';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { useAuthenticatedSession } from '@/authenticatedSession/AuthenticatedSessionContext';
import CaseAssistResult from './CaseAssistResult';
import MemberIdResults from './MemberIdResults';
import {
  convertCaseAssistInfoToAddEditMatterProps,
  findExistingAragMattersByCaseAssistNumber,
  type ExistingAragCaseMatch,
} from './util';
import { useNavigate } from 'react-router-dom';

interface Props {
  initialCaseAssistNumber?: string;
  initialMemberId?: string;
  onClose: () => void;
  onShowAddEditMatterModal: (props: {
    flattenedMatter: FlattenedMatter | undefined;
    overrides: Partial<AddEditMatterFormData> | undefined;
  }) => void;
}

type ExistingMatchesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; matches: ExistingAragCaseMatch[] }
  | { status: 'error'; error: string };

export const AragSearchModal = ({
  initialCaseAssistNumber,
  initialMemberId,
  onClose,
  onShowAddEditMatterModal,
}: Props) => {
  const authSession = useAuthenticatedSession();
  const userSelectOptions = useUserSelectOptions();
  const navigate = useNavigate();

  const [aragResponsibleAttorneyId, setAragResponsibleAttorneyId] =
    useState('');
  const [aragSearchType, setAragSearchType] = useState<'conf' | 'member'>(
    initialCaseAssistNumber ? 'conf' : initialMemberId ? 'member' : 'conf',
  );
  const [aragCaseAssistNumber, setAragCaseAssistNumber] = useState(
    initialCaseAssistNumber ?? '',
  );
  const [aragMemberId, setAragMemberId] = useState(initialMemberId ?? '');
  const [isAragSearching, setIsAragSearching] = useState(false);
  const [aragSearchError, setAragSearchError] = useState<
    | {
        message: string;
        userVisibleEventId?: string;
      }
    | undefined
  >();
  const [aragCaseAssistResult, setAragCaseAssistResult] = useState<
    CaseDetailsPage | undefined
  >();
  const [aragMemberResults, setAragMemberResults] = useState<
    GetAragCasesByMemberId.Case[] | undefined
  >();
  const [selectedFeeScheduleIndex, setSelectedFeeScheduleIndex] =
    useState<number>(0);
  const [shouldTriggerSearch, setShouldTriggerSearch] = useState(false);
  const [existingMatchesState, setExistingMatchesState] =
    useState<ExistingMatchesState>({ status: 'idle' });

  useEffect(() => {
    setAragCaseAssistNumber(initialCaseAssistNumber ?? '');
    if (initialCaseAssistNumber) {
      setAragSearchType('conf');
    }
  }, [initialCaseAssistNumber]);

  useEffect(() => {
    setAragMemberId(initialMemberId ?? '');
    if (initialMemberId) {
      setAragSearchType('member');
    }
  }, [initialMemberId]);

  useEffect(() => {
    if (!authSession || !aragCaseAssistResult) {
      setExistingMatchesState({ status: 'idle' });
      return;
    }
    const trimmedCaseAssistNumber = aragCaseAssistNumber.trim();
    if (!trimmedCaseAssistNumber) {
      setExistingMatchesState({ status: 'idle' });
      return;
    }

    let isCancelled = false;
    setExistingMatchesState({ status: 'loading' });

    findExistingAragMattersByCaseAssistNumber({
      authSession,
      caseAssistNumber: trimmedCaseAssistNumber,
    })
      .then((matches) => {
        if (isCancelled) {
          return;
        }
        setExistingMatchesState({ status: 'loaded', matches });
      })
      .catch((error) => {
        if (isCancelled) {
          return;
        }
        telemetry.error({
          error,
          message:
            'Failed to check for existing matters with ARAG CaseAssist number',
          extra: {
            caseAssistNumber: trimmedCaseAssistNumber,
          },
        });
        setExistingMatchesState({
          status: 'error',
          error:
            'Unable to check for existing matters right now. Please try again.',
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [authSession, aragCaseAssistResult, aragCaseAssistNumber]);

  const isSearchDisabled = useMemo(() => {
    if (!aragResponsibleAttorneyId) return true;
    if (aragSearchType === 'conf')
      return aragCaseAssistNumber.trim().length === 0;
    return aragMemberId.trim().length === 0;
  }, [
    aragResponsibleAttorneyId,
    aragSearchType,
    aragCaseAssistNumber,
    aragMemberId,
  ]);

  // Trigger search when shouldTriggerSearch flag is set and search is not disabled
  useEffect(() => {
    if (shouldTriggerSearch && !isSearchDisabled) {
      setShouldTriggerSearch(false);
      handleSearch();
    }
  }, [shouldTriggerSearch, isSearchDisabled]);

  const handleSearch = async () => {
    setAragSearchError(undefined);
    setAragCaseAssistResult(undefined);
    setAragMemberResults(undefined);
    setIsAragSearching(true);
    try {
      if (aragSearchType === 'conf') {
        const data = await getAragCaseByCaseAssistNumber({
          practistUserId: aragResponsibleAttorneyId,
          caseAssistNumber: aragCaseAssistNumber,
        });
        switch (data.status) {
          case 'success':
            setAragCaseAssistResult(data.payload);
            // Set default selected fee schedule to the last one
            if (
              data.payload.feeSchedule &&
              data.payload.feeSchedule.length > 0
            ) {
              setSelectedFeeScheduleIndex(data.payload.feeSchedule.length - 1);
            }
            break;
          case 'caseNotFound':
            setAragSearchError({
              message: `No case found`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          case 'aragCredentialsNotFound':
            setAragSearchError({
              message: `ARAG credentials not found`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          case 'internalError':
            setAragSearchError({
              message: `Search failed with internal error`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          default:
            setAragSearchError({
              message: `Search failed with unknown error`,
            });
            break;
        }
      } else {
        const data = await getAragCasesByMemberId({
          practistUserId: aragResponsibleAttorneyId,
          aragMemberId: aragMemberId,
        });
        switch (data.status) {
          case 'success':
            setAragMemberResults(data.payload);
            break;
          case 'multipleMembersFound':
            setAragSearchError({
              message: `Multiple possible matches found: ${data.memberIds.join(
                ', ',
              )}`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          case 'memberNotFound':
            setAragSearchError({
              message: `No member found`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          case 'casesNotFound':
            setAragSearchError({
              message: `No cases found`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          case 'internalError':
            setAragSearchError({
              message: `Search failed with internal error`,
              userVisibleEventId: data.userVisibleEventId,
            });
            break;
          default:
            const eventId = uuidv4();
            telemetry.error({
              error: new Error('Unexpected status from getAragCasesByMemberId'),
              extra: {
                userVisibleEventId: eventId,
                status: data.status,
              },
            });
            setAragSearchError({
              message: `Search failed with unknown error`,
              userVisibleEventId: eventId,
            });
            break;
        }
      }
    } catch (err) {
      const eventId = uuidv4();
      telemetry.error({
        error: err,
        message: 'Exception caught during search',
        extra: {
          userVisibleEventId: eventId,
        },
      });
      setAragSearchError({
        message: `Error during search`,
        userVisibleEventId: eventId,
      });
    } finally {
      setIsAragSearching(false);
    }
  };

  const handleShowAddEditMatterModal = async (
    matter: MatterDocument | undefined,
  ) => {
    if (!authSession) {
      throw new Error('No authenticated session');
    }
    if (!aragCaseAssistResult) {
      throw new Error('No case assist result');
    }
    const { flattenedMatter, overrides } =
      await convertCaseAssistInfoToAddEditMatterProps({
        authSession: authSession,
        matter: matter,
        caseAssistResult: aragCaseAssistResult,
        selectedFeeScheduleIndex,
        responsibleAttorneyId: aragResponsibleAttorneyId,
      });
    onShowAddEditMatterModal({ flattenedMatter, overrides });
    onClose();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      size='lg'
      radius='xl'
      withCloseButton={false}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap='lg' align='center' p='md'>
        <Title order={1} size='lg' c='grayText'>
          Find ARAG case details
        </Title>
        <Paper w='100%' className={styles.searchForm}>
          <Stack gap='lg' w='100%' align='center' p='xl'>
            <Stack gap='sm' w='100%'>
              <Select
                label='Responsible Attorney'
                placeholder='Select an attorney'
                data={userSelectOptions}
                value={aragResponsibleAttorneyId}
                onChange={(value) => setAragResponsibleAttorneyId(value ?? '')}
                required
              />

              <Select
                label='Search Type'
                required
                data={[
                  { value: 'conf', label: 'CaseAssist Number' },
                  { value: 'member', label: 'Member ID' },
                ]}
                value={aragSearchType}
                onChange={(value) =>
                  setAragSearchType((value as 'conf' | 'member') ?? 'conf')
                }
              />

              {aragSearchType === 'conf' ? (
                <TextInput
                  label='CaseAssist Number'
                  placeholder='e.g., 4368699-001'
                  value={aragCaseAssistNumber}
                  onChange={(e) => setAragCaseAssistNumber(e.target.value)}
                  required
                />
              ) : (
                <TextInput
                  label='Member ID'
                  placeholder='e.g., 205988537788'
                  value={aragMemberId}
                  onChange={(e) => setAragMemberId(e.target.value)}
                  required
                />
              )}
            </Stack>
            <Button
              leftSection={<SearchIcon size={18} />}
              loading={isAragSearching}
              disabled={isSearchDisabled}
              variant='outline'
              onClick={() => setShouldTriggerSearch(true)}
              maw='400px'
              w='100%'
            >
              Search cases
            </Button>
          </Stack>
        </Paper>

        {aragSearchError && (
          <Alert color='red' title='Search Error'>
            <Stack>
              <Text fw={500}>{aragSearchError.message}</Text>
              {aragSearchError.userVisibleEventId && (
                <CopyableText
                  value={`Event ID: ${aragSearchError.userVisibleEventId}`}
                  copyableValue={aragSearchError.userVisibleEventId}
                />
              )}
            </Stack>
          </Alert>
        )}

        {aragCaseAssistResult && (
          <CaseAssistResult
            caseAssistNumber={aragCaseAssistNumber}
            caseAssistResult={aragCaseAssistResult}
            selectedFeeScheduleIndex={selectedFeeScheduleIndex}
            onSelectedFeeScheduleIndexChange={setSelectedFeeScheduleIndex}
            onShowAddEditMatterModal={(matter) => {
              handleShowAddEditMatterModal(matter);
            }}
            existingMatches={
              existingMatchesState.status === 'loaded'
                ? existingMatchesState.matches
                : []
            }
            isCheckingExistingMatches={
              existingMatchesState.status === 'loading'
            }
            existingMatchesError={
              existingMatchesState.status === 'error'
                ? existingMatchesState.error
                : undefined
            }
            onViewMatter={(matterId) => {
              navigate(`/matter/${matterId}`);
              onClose();
            }}
          />
        )}

        {aragMemberResults && (
          <MemberIdResults
            results={aragMemberResults}
            onLoadDetailsRequested={(row) => {
              setAragSearchType('conf');
              setAragCaseAssistNumber(row.caseAssistNumber);
              setShouldTriggerSearch(true);
            }}
          />
        )}
      </Stack>
    </Modal>
  );
};

export default AragSearchModal;
